import './travel-tab.scss'
import React, { useCallback, useEffect, useState } from 'react'
import { Loader } from '@entur/loader'
import { BannerAlertBox } from '@entur/alert'
import { MonthPicker } from '../month-picker/MonthPicker'
import { TravelTypeFilterField } from './filter/travel-type-filter-field'
import { TravelerFilterField } from './filter/traveler-filter-field'
import { TravelCard } from './card/travel-card'
import { EmployeeTickets, EmployeeTripsResponse, FetchState, SavingTicket } from '../../api-clients/api-types'
import { betweenDates } from './savings-calculations'
import { useApi } from '../../api-clients/client-hooks'
import { getEmployeeTrips } from '../../api-clients/eba-clients'
import { useEmployee } from '../../contexts/employee-context'
import { useClientInfo } from '../../contexts/client-info-context'
import { TabIndex } from '../../pages/employee-page'
import { TravelTable } from './table/travel-table'
import { TravelViewField } from './travel-view-field'

export type Period = {
  isPeriodicTicket: boolean
  startDate: Date
  endDate: Date
  cancelDate?: Date
}

const toEmployeeTickets = (employeeTrips: EmployeeTripsResponse[]) => {
  return employeeTrips.flatMap((employeeTrip) => {
    const travels = employeeTrip.travels.map((travel) => {
      return {
        ...travel,
        name: `${employeeTrip.firstName} ${employeeTrip.surname}`,
        lastTravelDate: new Date(travel.lastTravelDate),
        travelDate: new Date(travel.travelDate),
        timestamp: new Date(travel.timestamp),
        effectiveCancellationDate: travel.effectiveCancellationDate
          ? new Date(travel.effectiveCancellationDate)
          : undefined,
      }
    })
    return { ...employeeTrip, travels: travels }
  })
}

type TravelTabProps = {
  tabIndex: TabIndex
}

export const TravelTab: React.FC<TravelTabProps> = ({ tabIndex }) => {
  const [employeeTripsCacheMap] = useState<Map<number, EmployeeTickets[]>>(new Map())
  const [isTable, setIsTable] = useState(false)

  const [month, setMonth] = useState(0)
  const [year, setYear] = useState(new Date().getFullYear())
  const [changedYear, setChangedYear] = useState(true)

  const [travelTypes, setTravelTypes] = useState<string[]>([])
  const [travelersName, setTravelersName] = useState<string[]>([])

  const [employeeTickets, setEmployeeTickets] = useState<EmployeeTickets[] | undefined>(undefined)
  const [employeeTripsResponse, fetchEmployeeTrips] = useApi(getEmployeeTrips, {
    successCallback: (data) => {
      employeeTripsCacheMap.set(year, toEmployeeTickets(data))
      setEmployeeTickets(toEmployeeTickets(data))
    },
  })

  const { employee } = useEmployee()
  const { hasSensitiveAccess } = useClientInfo()

  const filterEmployeeTickets = (employeeTickets: EmployeeTickets[]) => {
    const isPrivateTicket = (ticket: SavingTicket) => travelTypes.includes('privat') && !ticket.isWorkTrip
    const isWorkTicket = (ticket: SavingTicket) => travelTypes.includes('tjeneste') && ticket.isWorkTrip

    return employeeTickets
      .flatMap((data) => {
        return data.travels
      })
      .filter((ticket) => betweenDates(month, year, new Date(ticket.travelDate), new Date(ticket.lastTravelDate)))
      .filter((ticket) => travelTypes.length === 0 || isPrivateTicket(ticket) || isWorkTicket(ticket))
      .filter((ticket) => travelersName.length === 0 || travelersName.includes(ticket.name ?? ''))
  }

  const fetchEmployeeTripsCached = React.useCallback(() => {
    if (!employeeTripsCacheMap.has(year)) {
      fetchEmployeeTrips({ id: employee.id, year: year.toString() })
    } else {
      setEmployeeTickets(employeeTripsCacheMap.get(year))
    }
  }, [employee, employeeTripsCacheMap, fetchEmployeeTrips, year])

  useEffect(() => {
    if (changedYear && hasSensitiveAccess && tabIndex === TabIndex.TRAVEL) {
      setChangedYear(false)
      fetchEmployeeTripsCached()
    }
  }, [changedYear, employee.id, fetchEmployeeTrips, fetchEmployeeTripsCached, hasSensitiveAccess, tabIndex, year])

  const updateYear = useCallback((year: number) => {
    setYear(year)
    setChangedYear(true)
  }, [])

  const savingTickets: SavingTicket[] | undefined = employeeTickets
    ? filterEmployeeTickets(employeeTickets)
    : undefined

  const travelCardElements =
    savingTickets &&
    (isTable ? (
      <TravelTable tickets={savingTickets} year={year} month={month}></TravelTable>
    ) : (
      savingTickets.map((value) => {
        return (
          <li style={{ listStyle: 'none' }} key={`${value.orderLineId} - ${value.name}`}>
            <TravelCard year={year} month={month} ticket={value} />
          </li>
        )
      })
    ))

  if (!hasSensitiveAccess) {
    return (
      <BannerAlertBox data-cy="travel-error-banner-alert" variant="error">
        Ikke tilgang til å se reiser
      </BannerAlertBox>
    )
  }

  return (
    <>
      {employeeTripsResponse.status === FetchState.ERROR && (
        <BannerAlertBox
          data-cy="travel-error-banner-alert"
          title={`Henting av reiser for ${year} feilet`}
          variant="error"
        >
          Prøv på nytt eller ta kontakt med Entur support om det vedvarer.
        </BannerAlertBox>
      )}
      <MonthPicker month={month} year={year} setYear={updateYear} setMonth={setMonth} />
      <div className="filter-list">
        <TravelViewField
          travelView={isTable ? 'tabell' : 'normal'}
          setTravelView={(travelView) => setIsTable(travelView === 'tabell')}
        />
        <TravelTypeFilterField travelTypes={travelTypes} setTravelTypes={setTravelTypes} />
        <TravelerFilterField setTravelTypes={setTravelersName} travelTypes={travelersName} />
      </div>
      {employeeTripsResponse.status === FetchState.LOADING && <Loader>Henter reiser</Loader>}
      {employeeTripsResponse.status === FetchState.SUCCESS && (
        <ul className="ticket-list">{travelCardElements}</ul>
      )}
    </>
  )
}
